import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook"; // Updated import
import {
  DivisionsDropdown,
  AdminUsersDropdown,
} from "../../CourseCatalog/DropDowns";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const Add = ({ onRecordAdded }) => {
  // Use useFirestore hook for "Fps" collection
  const {
    addData,
    loading: firestoreLoading,
    error: firestoreError,
  } = useFirestore("Fps");

  // Use useFirestore hook for "FpsCourses" collection to get available courses
  const { data: coursesData, loading: coursesLoading } =
    useFirestore("FpsCourses");

  // Use useFirestore hook for "Divisions" collection to get available divisions
  const { data: divisionsData, loading: divisionsLoading } =
    useFirestore("Divisions");

  // Use useFirestore hook for "users" collection to get admin users
  const { data: usersData, loading: usersLoading } = useFirestore("users");

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [tartanIdError, setTartanIdError] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [formData, setFormData] = useState({
    Active: 0,
    Last_Name: "",
    First_name: "",
    Full_Name: "",
    Courses: "",
    Career_Days: 0,
    Sinclair_Department: "",
    Title: "",
    Sinclair_Location: "",
    Sinclair_Phone: "",
    Sinclair_Email: "",
    Project_Manager_Name: "",
    Project_Manager_Id: "",
    Sinclair_Division: "",
    Sinclair_Division_abv: "",
    Note: "",
    Tartan_Id: "",
  });

  // Auto-populate Full_Name when First_name or Last_Name changes
  useEffect(() => {
    const fullName = `${formData.First_name} ${formData.Last_Name}`.trim();
    if (fullName !== formData.Full_Name) {
      setFormData((prevData) => ({
        ...prevData,
        Full_Name: fullName,
      }));
    }
  }, [formData.First_name, formData.Last_Name]);

  // Update Courses field when selectedCourses changes
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      Courses: selectedCourses.join(", "),
    }));
  }, [selectedCourses]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle Sinclair Division change to auto-populate abbreviation
    if (name === "Sinclair_Division" && divisionsData) {
      let abbreviation = "";

      if (value) {
        const selectedDivision = divisionsData.find((division) => {
          const divisionName =
            division.DivisionName || division.Division_Name || division.Name;
          return (
            divisionName &&
            divisionName.trim().toLowerCase() === value.trim().toLowerCase()
          );
        });

        // Look for various abbreviation field names
        abbreviation =
          selectedDivision?.Abbreviation ||
          selectedDivision?.abbreviation ||
          selectedDivision?.DivisionAbbreviation ||
          selectedDivision?.Division_Abbreviation ||
          selectedDivision?.DivisionCode ||
          selectedDivision?.Division_Code ||
          selectedDivision?.DivisionID ||
          selectedDivision?.DivisionId ||
          selectedDivision?.Division_Id ||
          selectedDivision?.Code ||
          "";
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        Sinclair_Division_abv: abbreviation,
      }));
    }
    // Handle Pathway Manager Name change to auto-populate ID
    else if (name === "Project_Manager_Name" && usersData) {
      let managerId = "";

      if (value) {
        const selectedManager = usersData.find(
          (user) =>
            user.name === value &&
            (user.techPrepAdminFlag === true || user.userTypes?.admin === true),
        );

        managerId = selectedManager?.id || "";
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        Project_Manager_Id: managerId,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
      // Validate Tartan ID length inline
      if (name === "Tartan_Id") {
        setTartanIdError(
          value && value.trim().length > 0 && value.trim().length !== 7
            ? "Tartan ID must be exactly 7 digits"
            : "",
        );
      }
    }
  };

  const handleCoursesChange = (event) => {
    const value = event.target.value;
    setSelectedCourses(typeof value === "string" ? value.split(",") : value);
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked ? -1 : 0,
    }));
  };

  const handleSubmit = async () => {
    // Validate Tartan ID before submitting
    const tartanVal = formData.Tartan_Id ? formData.Tartan_Id.trim() : "";
    if (tartanVal.length > 0 && tartanVal.length !== 7) {
      setTartanIdError("Tartan ID must be exactly 7 digits");
      return;
    }
    if (tartanVal.length === 0) {
      setTartanIdError("Tartan ID is required (7 digits)");
      return;
    }
    setTartanIdError("");
    setLoading(true);
    try {
      await addData(formData); // Use addData from useFirestore
      setLoading(false);
      setSnackbarOpen(true);
      setFormData({
        Active: 0,
        Last_Name: "",
        First_name: "",
        Full_Name: "",
        Courses: "",
        Career_Days: 0,
        Sinclair_Department: "",
        Title: "",
        Sinclair_Location: "",
        Sinclair_Phone: "",
        Sinclair_Email: "",
        Project_Manager_Name: "",
        Project_Manager_Id: "",
        Sinclair_Division: "",
        Sinclair_Division_abv: "",
        Note: "",
        Tartan_Id: "",
      });
      setSelectedCourses([]);
      setOpen(false);
      onRecordAdded(); // Call the callback function to refresh the list
    } catch (error) {
      console.error("Error adding record:", error);
      // Optionally, display a more user-friendly error message based on 'error'
      // For example, using another snackbar or an alert.
      // alert(`Error adding record: ${error.message}`);
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <div>
      <Button
        variant="contained"
        color="success"
        onClick={() => setOpen(true)}
        sx={{ marginBottom: "10px", color: "white" }} // Ensure button is not disabled by firestoreLoading if it's not relevant here
      >
        Add Record
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <ModalDialogTitle onClose={() => setOpen(false)}>
          Add Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                name="Last_Name"
                value={formData.Last_Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                name="First_name"
                value={formData.First_name}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Full Name"
                name="Full_Name"
                value={formData.Full_Name}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                  style: { color: "#666", backgroundColor: "#f5f5f5" },
                }}
                disabled
                helperText="Auto-generated from First and Last Name"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="courses-select-label">Courses</InputLabel>
                <Select
                  labelId="courses-select-label"
                  id="courses-select"
                  multiple
                  value={selectedCourses}
                  onChange={handleCoursesChange}
                  label="Courses"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  disabled={coursesLoading}
                >
                  {coursesLoading && (
                    <MenuItem disabled>Loading courses...</MenuItem>
                  )}
                  {!coursesLoading &&
                    (!coursesData || coursesData.length === 0) && (
                      <MenuItem disabled>No courses available</MenuItem>
                    )}
                  {coursesData &&
                    coursesData
                      .filter((course) => course.Active === "Yes") // Only show courses where Active is "Yes"
                      .sort((a, b) =>
                        (a.Course || "").localeCompare(b.Course || ""),
                      ) // Sort alphabetically by Course name
                      .map((course) => (
                        <MenuItem key={course.id} value={course.Course}>
                          {course.Course}{" "}
                          {course.Subject ? `- ${course.Subject}` : ""}
                        </MenuItem>
                      ))}
                </Select>
                {coursesLoading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 1 }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </FormControl>
            </Grid>{" "}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Department"
                name="Sinclair_Department"
                value={formData.Sinclair_Department}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Title"
                name="Title"
                value={formData.Title}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Location"
                name="Sinclair_Location"
                value={formData.Sinclair_Location}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                name="Sinclair_Phone"
                value={formData.Sinclair_Phone}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="Sinclair_Email"
                value={formData.Sinclair_Email}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AdminUsersDropdown
                value={formData.Project_Manager_Name}
                onChange={handleChange}
                name="Project_Manager_Name"
                label="Pathway Manager Name"
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pathway Manager ID"
                name="Project_Manager_Id"
                value={formData.Project_Manager_Id}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                  style: { color: "#666", backgroundColor: "#f5f5f5" },
                }}
                disabled
                helperText="Auto-populated based on manager selection"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DivisionsDropdown
                value={formData.Sinclair_Division}
                onChange={handleChange}
                name="Sinclair_Division"
                label="Division"
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Division Abbreviation"
                name="Sinclair_Division_abv"
                value={formData.Sinclair_Division_abv}
                fullWidth
                margin="normal"
                InputProps={{
                  readOnly: true,
                  style: { color: "#666", backgroundColor: "#f5f5f5" },
                }}
                disabled
                helperText="Auto-populated based on division selection"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Note"
                name="Note"
                value={formData.Note}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tartan ID"
                name="Tartan_Id"
                value={formData.Tartan_Id}
                onChange={handleChange}
                fullWidth
                margin="normal"
                error={!!tartanIdError}
                helperText={tartanIdError || "Must be exactly 7 digits"}
                inputProps={{ maxLength: 7 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active === -1}
                    onChange={handleCheckboxChange}
                    name="Active"
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Career_Days === -1}
                    onChange={handleCheckboxChange}
                    name="Career_Days"
                  />
                }
                label="Career Days"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          saveDisabled={firestoreLoading}
        />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Record has been added"
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          sx: {
            backgroundColor: "black",
            color: "white",
          },
        }}
      />
    </div>
  );
};

export default Add;
